import { useEffect, useState } from "react";
import { WheelSheet, WheelColumn } from "./WheelPicker";

/**
 * Item detail sheet — pops up when an item needs specs (couch size, mattress size, TV size, etc.)
 * Reads catalog live so the source of truth is the backend.
 */
export function ItemDetailsSheet({ open, itemName, catalog, value, onCancel, onDone }) {
  const meta = catalog?.items?.find((i) => i.name === itemName);
  const [spec, setSpec] = useState(value?.spec || meta?.options?.[0]?.id || "");
  const [addons, setAddons] = useState(value?.addons || []);

  useEffect(() => {
    if (!open) return;
    setSpec(value?.spec || meta?.options?.[0]?.id || "");
    setAddons(value?.addons || []);
  }, [open, itemName]); // eslint-disable-line

  if (!meta) return null;

  const done = () => onDone({ spec, addons });
  const toggle = (id) => setAddons((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);

  return (
    <WheelSheet open={open} title={meta.label} onCancel={onCancel} onDone={done}>
      <div className="flex-1 min-w-0">
        <WheelColumn className="flex-1 w-full"
          options={meta.options.map((o) => ({ value: o.id, label: o.label }))}
          value={spec} onChange={setSpec} />
        {meta.addons?.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center px-4 py-3">
            {meta.addons.map((a) => (
              <button key={a.id} type="button" onClick={() => toggle(a.id)}
                data-testid={`item-addon-${a.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  addons.includes(a.id)
                    ? "border-[var(--cc-accent)] bg-[var(--cc-accent)]/15 text-[var(--cc-accent)]"
                    : "text-muted-foreground hover:border-foreground/40"}`}>
                + {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </WheelSheet>
  );
}

/** Simple sheet for arrival time — matches the Mission Date wheel */
export function TimeWheelSheet({ open, value, onCancel, onDone, title = "Arrival Time" }) {
  const parseInit = () => {
    const [h = 9, m = 0] = String(value || "09:00").split(":").map(Number);
    return { h12: ((h + 11) % 12) + 1, m: Math.round(m / 15) * 15 % 60, ap: h >= 12 ? "PM" : "AM" };
  };
  const [h12, setH12] = useState(parseInit().h12);
  const [m, setM] = useState(parseInit().m);
  const [ap, setAp] = useState(parseInit().ap);

  useEffect(() => {
    if (!open) return;
    const init = parseInit();
    setH12(init.h12); setM(init.m); setAp(init.ap);
  }, [open, value]); // eslint-disable-line

  const done = () => {
    let h24 = h12 % 12; if (ap === "PM") h24 += 12;
    onDone(`${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };
  const hours = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
  const mins = [0, 15, 30, 45].map((v) => ({ value: v, label: String(v).padStart(2, "0") }));
  const aps = [{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }];

  return (
    <WheelSheet open={open} title={title} onCancel={onCancel} onDone={done}>
      <WheelColumn className="flex-1" options={hours} value={h12} onChange={setH12} />
      <WheelColumn className="flex-1" options={mins} value={m} onChange={setM} />
      <WheelColumn className="flex-1" options={aps} value={ap} onChange={setAp} />
    </WheelSheet>
  );
}

export function fmtTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}
